import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";

const RED = "#d31f46";
const GREEN = "#46bd70";
const BLUE = "#2e9bd8";
const YELLOW = "#f4dd33";

// The central "junction" mark: a thick track that meets at a 3-way hub — a
// horizontal left arm, a tall vertical block at the top-right, and a stem
// hanging beneath. The top edge scoops down into a valley between the arms;
// the bottom-right edge sweeps convexly into the stem.
const MARK_PATH = [
  "M 122 178",
  "Q 122 158 142 158", // left arm: top-left corner
  "L 262 158",
  "Q 282 158 285 187", // top edge ends, scoop begins
  "C 295 298 338 356 410 356", // concave scoop down to valley floor
  "C 502 356 566 290 594 224", // ribbon rises toward right block
  "C 606 196 610 190 610 168", // ease into vertical left edge
  "L 610 70",
  "Q 610 48 630 48", // right block: top-left corner
  "L 780 48",
  "Q 800 48 800 68", // right block: top-right corner
  "L 800 214",
  "Q 800 235 779 235", // right edge ends, convex sweep begins
  "C 700 232 646 250 596 302", // convex sweep down-left
  "C 556 344 508 360 506 400", // eases into the stem's right wall
  "L 506 525",
  "Q 506 545 486 545", // stem: bottom-right corner
  "L 340 545",
  "Q 320 545 320 525", // stem: bottom-left corner
  "L 320 400",
  "Q 320 360 288 360", // concave fillet into left arm underside
  "L 142 360",
  "Q 122 360 122 340", // left arm: bottom-left corner
  "Z",
].join(" ");

const useStyles = makeStyles({
  root: {
    width: "100%",
    height: "100%",
    display: "block",
    overflow: "visible",
  },
  markFill: {
    fill: tokens.colorNeutralForeground1,
  },
  // gentle breathing for the central mark
  mark: {
    transformBox: "fill-box",
    transformOrigin: "center",
    animationName: {
      "0%, 100%": { transform: "scale(1)", opacity: 0.92 },
      "50%": { transform: "scale(1.04)", opacity: 1 },
    },
    animationDuration: "1800ms",
    animationTimingFunction: "cubic-bezier(0.45, 0, 0.55, 1)",
    animationIterationCount: "infinite",
  },
  square: {
    transformBox: "fill-box",
    transformOrigin: "center",
    animationName: {
      "0%": { transform: "scale(0.2) rotate(-25deg)", opacity: 0 },
      "18%": { transform: "scale(1.12) rotate(3deg)", opacity: 1 },
      "34%": { transform: "scale(1) rotate(0deg)", opacity: 1 },
      "70%": { transform: "scale(1) rotate(0deg)", opacity: 1 },
      "88%, 100%": { transform: "scale(0.2) rotate(-25deg)", opacity: 0 },
    },
    animationDuration: "2400ms",
    animationTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
    animationIterationCount: "infinite",
  },
  delayRed: { animationDelay: "0ms" },
  delayGreen: { animationDelay: "300ms" },
  delayYellow: { animationDelay: "600ms" },
  delayBlue: { animationDelay: "900ms" },
});

export function AnimatedLogo({ className }: { className?: string }) {
  const styles = useStyles();
  const { t } = useTranslation();

  return (
    <svg
      className={mergeClasses(styles.root, className)}
      viewBox="0 0 868 868"
      role="img"
      aria-label={t("common.labels.loading")}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className={mergeClasses(styles.mark, styles.markFill)}
        d={MARK_PATH}
      />

      <rect
        className={mergeClasses(styles.square, styles.delayRed)}
        x="408"
        y="8"
        width="92"
        height="92"
        fill={RED}
      />
      <rect
        className={mergeClasses(styles.square, styles.delayGreen)}
        x="693"
        y="410"
        width="108"
        height="108"
        fill={GREEN}
      />
      <rect
        className={mergeClasses(styles.square, styles.delayBlue)}
        x="48"
        y="608"
        width="142"
        height="142"
        fill={BLUE}
      />
      <rect
        className={mergeClasses(styles.square, styles.delayYellow)}
        x="452"
        y="660"
        width="188"
        height="188"
        fill={YELLOW}
      />
    </svg>
  );
}
